import TencentCloudChat, {ChatSDK} from '@tencentcloud/chat';
import {postChatEnd, postChatMessage} from "./connector";
import moment from "moment";
import {BEMessageType, MessageType} from "./type";


export const createChat = async (params: {
    appId: number;
    userId: string;
    userSig: string;
}): Promise<ChatSDK> => {
    try {
        let chat = TencentCloudChat.create({
            SDKAppID: params.appId || 20021923,
        });
        chat.setLogLevel(2);
        const result = await chat.login({
            userID: params.userId,
            userSig: params.userSig,
        }).then(value => value.data.errorCode === 0).catch(reason => {
            console.error(reason);
            return null;
        });
        return !!result ? chat : null;
    } catch (e) {
        console.error(e);
        return null;
    }
}

export const sentChatMessage = async (chat: ChatSDK, toId: string, message: BEMessageType) => {
    try {
        const beMessage = {
            tencentUserId: message.tencentUserId,
            bookingId: message.bookingId,
            ...(message?.mediaUrls?.length ? {
                mediaUrls: message.mediaUrls,
                mediaCaption: message.mediaCaption || undefined,
            } : {
                contents: message.contents,
            }),
            name: message?.name,
            threadId: message?.threadId,
            tencentGroupId: message?.tencentGroupId,
            type: message?.type,
        };
        const tencentMessage = chat.createTextMessage({
            to: toId,
            conversationType: TencentCloudChat.TYPES.CONV_GROUP,
            payload: {
                text: JSON.stringify({
                    ...beMessage,
                    eventName: "MESSAGE",
                    tencentGroupId: message.tencentGroupId,
                    threadId: message.threadId,
                    name: message.name,
                    type: message?.type,
                }),
            },
            needReadReceipt: true
        });
        const result = await postChatMessage(beMessage);
        if (!!result && result?.status === 200) {
            // send request to BE
            const resultTencent = await chat.sendMessage(tencentMessage).catch(reason => {
                return {
                    message: reason?.message,
                    code: reason?.code,
                    status: 500
                };
            });
            return resultTencent?.status !== 500 ? result : {
                message: resultTencent?.message || "Unknown error",
                code: resultTencent?.code || -1,
                status: 500,
                data: null
            };
        }
        return result;
    } catch (e) {
        console.error("Error send message:", e);
        return null
    }
}

export const sentEndChatMessage = async (chat: ChatSDK, toId: string, message: BEMessageType) => {
    try {
        const beMessage = {
            tencentUserId: message.tencentUserId,
            bookingId: message.bookingId,
            eventName: "END-CHAT",
            tencentGroupId: message.tencentGroupId,
            threadId: message.threadId,
            name: message.name,
        };
        const tencentMessage = chat.createTextMessage({
            to: toId,
            conversationType: TencentCloudChat.TYPES.CONV_GROUP,
            payload: {
                text: JSON.stringify(beMessage),
            },
            needReadReceipt: true
        });
        const result = await chat.sendMessage(tencentMessage, {
            messageControlInfo: {
                excludedFromUnreadCount: true,
                excludedFromLastMessage: true
            }
        }).catch(reason => null);
        // send request end chat to BE
        return await postChatEnd({
            bookingId: message.bookingId,
            threadId: message.threadId,
            tencentGroupId: message.tencentGroupId,
            tencentUserId: message.tencentUserId,
            name: message.name,
        });
    } catch (e) {
        return null;
    }
}

export const convertMediaUrlsToFiles = (mediaUrls: Array<string>) => {
    const files = [];
    mediaUrls?.forEach((item) => {
        const fileUrl = item;
        const fileInfo: any = {};
        if (!!fileUrl) {
            // format http://example.com..../fileSize/fileName.extension
            const fileName = fileUrl.split('/').pop().split("?")[0];
            const fileSize = fileUrl.split('/').slice(-2)[0];
            const fileExtension = fileName.split('.').pop();
            const fileNameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
            fileInfo.fileName = fileNameWithoutExtension;
            fileInfo.fileSize = parseInt(fileSize) || 0;
            fileInfo.fileExtension = fileExtension;
            fileInfo.file = fileUrl;
            fileInfo.type = ["jpg", "jpeg", "png", "gif"].includes(fileExtension) ? 'image' : 'document';
        }
        files.push(fileInfo);
    });
    return files;
}

export const convertTimeMessageToText = (timeInt: number) => {
    try {
        // convert time to timeText if time>24h from now => format HH:mm else format DD/MM/YYYY
        const time = moment(timeInt);
        const timeText = time.isAfter(moment().subtract(24, 'hours')) ? time.fromNow().replace("a few seconds ago", "Just now") : time.format("DD/MM/YYYY HH:mm");
        return timeText + "";
    } catch (e) {
        return "";
    }
}

export const convertBEToMessage = (userId: string, message: BEMessageType, mode?: string): MessageType => {
    const files = convertMediaUrlsToFiles(message?.mediaUrls || []);
    // convert time to timeText if time>24h from now => format HH:mm else format DD/MM/YYYY
    const time = moment(message?.createdAt || Date.now());

    const result: MessageType = {
        ...message,
        id: message.id,
        text: message.contents || message.mediaCaption || "",
        files,
        blobs: message?.mediaBlobUrls,
        type: files?.[0]?.type || 'text',
        time: time,
        timeInt: time.toDate().getTime(),
        status: message.status,
        mode: userId === message.tencentUserId || (mode === 'admin' && message?.type === 'admin') ? "sent" : "received",
    }
    return result;
}

export async function imageUrlToBlobUrl(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
}
